import React, { useState, useEffect, KeyboardEvent } from 'react';
import { ArrowLeft, ExternalLink, User, Building, Target, Clock, MessageSquare, Briefcase, Calendar, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { DURATION_OPTIONS, BRAND_VIBE_OPTIONS, CLIENT_ACQUISITION_OPTIONS, CLIENT_COUNT_OPTIONS, LONG_TERM_OPTIONS, MARKETING_TIME_OPTIONS, SURVEY_STATUS_OPTIONS } from '../../constants/survey';

interface SurveyResponse {
  id: string;
  user_id?: string;
  name?: string;
  email?: string | null;
  linkedin_profile: string;
  linkedin_full_url?: string;
  duration: string;
  brand_vibe: string;
  client_acquisition: string;
  client_count: string;
  long_term: string;
  marketing_time: string;
  additional_notes: string;
  status: string;
  created_at: string;
  submitted_at?: string;
}

interface SurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  response: SurveyResponse | null;
  onSuccess: () => void;
}

const SurveyModal: React.FC<SurveyModalProps> = ({
  isOpen,
  onClose,
  response,
  onSuccess,
}) => {
  const [status, setStatus] = useState('');
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (response) {
      setStatus(response.status);
      setEmail(response.email ?? '');
    }
  }, [response]);

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey as any);
    return () => {
      document.removeEventListener('keydown', handleEscKey as any);
    };
  }, [isOpen, onClose]);

  const normalizedEmail = email.trim().toLowerCase();
  const originalEmail = (response?.email ?? '').trim().toLowerCase();
  const emailChanged = normalizedEmail !== originalEmail;
  const statusChanged = Boolean(response && status !== response.status);
  const canSave = Boolean(response) && (emailChanged || statusChanged);

  const handleSave = async () => {
    if (!response || !canSave) return;

    if (normalizedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      toast.error('Enter a valid email address');
      return;
    }

    setIsSaving(true);
    try {
      const updateData: Record<string, unknown> = {
        email: normalizedEmail || null,
        status,
      };

      if (status === 'submitted' && response.status !== 'submitted') {
        updateData.submitted_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('cto_survey')
        .update(updateData)
        .eq('id', response.id);

      if (error) throw error;

      toast.success('Survey contact updated');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error updating survey: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLinkedInClick = () => {
    if (!response) return;
    const url = response.linkedin_full_url || `https://linkedin.com/in/${response.linkedin_profile}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fieldDescriptions = {
    duration: "How long have you been fractional?",
    brand_vibe: "What's your brand vibe?",
    client_acquisition: "Do you do outreach or mostly inbound?",
    client_count: "How many clients do you juggle?",
    long_term: "Is this the long game or in-between?",
    marketing_time: "How do you think about time for marketing?",
    additional_notes: "Any additional notes or specific challenges?"
  };

  if (!response) return null;

  return (
    <div 
      className={`fixed inset-y-0 right-0 w-full md:w-1/2 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } z-50`}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">
              Survey Response Details
            </h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-emerald-500" />
                Contact Information
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {response.name || 'Not provided'}
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="cto-survey-email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Email
                  </label>
                  <input
                    id="cto-survey-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Paste researched email for import"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    autoComplete="off"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Optional — fill before importing into subscribers.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    LinkedIn Profile
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleLinkedInClick}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center"
                    >
                      {response.linkedin_profile}
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </button>
                    <button
                      onClick={() => window.open(`https://questions.stephansmith.solutions/${response.linkedin_profile}`, '_blank', 'noopener,noreferrer')}
                      className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 flex items-center text-sm"
                    >
                      Survey
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Created
                  </label>
                  <p className="text-gray-900 dark:text-white flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                    {formatDate(response.created_at)}
                  </p>
                </div>
                {response.submitted_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Submitted
                    </label>
                    <p className="text-gray-900 dark:text-white flex items-center">
                      <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                      {formatDate(response.submitted_at)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Status Management */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Status Management
              </h3>
              <div className="flex items-center gap-3">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {Object.entries(SURVEY_STATUS_OPTIONS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !canSave}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Survey Responses */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-emerald-500" />
                Survey Responses
              </h3>

              {Object.entries(fieldDescriptions).map(([field, description]) => (
                <div key={field} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                      {field === 'duration' && <Clock className="w-4 h-4 mr-2 text-gray-400" />}
                      {field === 'brand_vibe' && <Building className="w-4 h-4 mr-2 text-gray-400" />}
                      {field === 'client_acquisition' && <Target className="w-4 h-4 mr-2 text-gray-400" />}
                      {field === 'client_count' && <User className="w-4 h-4 mr-2 text-gray-400" />}
                      {field === 'long_term' && <Target className="w-4 h-4 mr-2 text-gray-400" />}
                      {field === 'marketing_time' && <Clock className="w-4 h-4 mr-2 text-gray-400" />}
                      {field === 'additional_notes' && <MessageSquare className="w-4 h-4 mr-2 text-gray-400" />}
                      {description}
                    </h4>
                  </div>
                  {field === 'brand_vibe' ? (
                    <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded border">
                      {BRAND_VIBE_OPTIONS[response[field as keyof SurveyResponse] as keyof typeof BRAND_VIBE_OPTIONS] || response[field as keyof SurveyResponse] || 'No response provided'}
                    </div>
                  ) : field === 'duration' ? (
                    <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded border">
                      {DURATION_OPTIONS[response[field as keyof SurveyResponse] as keyof typeof DURATION_OPTIONS] || response[field as keyof SurveyResponse] || 'No response provided'}
                    </div>
                  ) : field === 'client_acquisition' ? (
                    <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded border">
                      {CLIENT_ACQUISITION_OPTIONS[response[field as keyof SurveyResponse] as keyof typeof CLIENT_ACQUISITION_OPTIONS] || response[field as keyof SurveyResponse] || 'No response provided'}
                    </div>
                  ) : field === 'client_count' ? (
                    <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded border">
                      {CLIENT_COUNT_OPTIONS[response[field as keyof SurveyResponse] as keyof typeof CLIENT_COUNT_OPTIONS] || response[field as keyof SurveyResponse] || 'No response provided'}
                    </div>
                  ) : field === 'long_term' ? (
                    <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded border">
                      {LONG_TERM_OPTIONS[response[field as keyof SurveyResponse] as keyof typeof LONG_TERM_OPTIONS] || response[field as keyof SurveyResponse] || 'No response provided'}
                    </div>
                  ) : field === 'marketing_time' ? (
                    <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded border">
                      {MARKETING_TIME_OPTIONS[response[field as keyof SurveyResponse] as keyof typeof MARKETING_TIME_OPTIONS] || response[field as keyof SurveyResponse] || 'No response provided'}
                    </div>
                  ) : (
                    <p className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded border">
                      {response[field as keyof SurveyResponse] || 'No response provided'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyModal;